import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import DateField from "@/components/DateField";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Download, Sparkles, Pencil, Trash2, ArrowRight, Zap, Calendar, Send } from "lucide-react";
import { useAuth, can } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";

const STATUS_TONE = {
  aangemeld: "bg-blue-50 text-blue-700 border-blue-200",
  wachtend: "bg-amber-50 text-amber-700 border-amber-200",
  toegewezen: "bg-emerald-50 text-emerald-700 border-emerald-200",
  afgemeld: "bg-neutral-100 text-neutral-600 border-neutral-200",
};

export default function WachtlijstPage() {
  const { user } = useAuth();
  const canWrite = can(user, "wachtlijst", "write");
  const canExport = can(user, "exports", "read");
  const [rows, setRows] = useState([]);
  const [locations, setLocations] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(init());
  const [suggestions, setSuggestions] = useState([]);
  const [aanmeldingenWl, setAanmeldingenWl] = useState([]);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    const [w, l, s, a] = await Promise.all([
      api.get("/wachtlijst"),
      api.get("/locations"),
      api.get("/wachtlijst/suggestions").catch(() => ({ data: { suggestions: [] } })),
      api.get("/aanmeldingen?status=wachtlijst").catch(() => ({ data: { items: [] } })),
    ]);
    setRows(w.data); setLocations(l.data);
    setSuggestions(s.data.suggestions || []);
    setAanmeldingenWl(a.data.items || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openEdit = (r) => {
    setEditingId(r.id);
    setForm({
      kind_voornaam: r.kind_voornaam || "", kind_achternaam: r.kind_achternaam || "",
      kind_geboortedatum: r.kind_geboortedatum || "",
      ouder_naam: r.ouder_naam || "", ouder_email: r.ouder_email || "", ouder_telefoon: r.ouder_telefoon || "",
      voorkeur_locatie_id: r.voorkeur_locatie_id || "",
      voorkeur_startdatum: r.voorkeur_startdatum || "",
      dagdelen_per_week: r.dagdelen_per_week ?? 2,
      status: r.status || "aangemeld", notitie: r.notitie || "",
    });
    setOpen(true);
  };
  const closeDialog = () => { setOpen(false); setEditingId(null); setForm(init()); };

  const submit = async () => {
    try {
      if (editingId) {
        await api.put(`/wachtlijst/${editingId}`, form);
        toast.success("Aanmelding bijgewerkt");
      } else {
        await api.post("/wachtlijst", form);
        toast.success("Aanmelding toegevoegd");
      }
      closeDialog(); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeRow = async (r) => {
    if (!window.confirm(`Verwijder aanmelding van ${r.kind_voornaam} ${r.kind_achternaam}?`)) return;
    try {
      await api.delete(`/wachtlijst/${r.id}`);
      toast.success("Aanmelding verwijderd");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const download = async (name) => {
    try {
      const r = await api.get(`/exports/${name}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `${name}_export.csv`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`${name.toUpperCase()} export gedownload`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const locMap = Object.fromEntries(locations.map(l => [l.id, l.name]));

  const columns = [
    { key: "kind_achternaam", label: "Kind", render: r => (
      <div>
        <div className="font-medium text-neutral-800">{r.kind_voornaam} {r.kind_achternaam}</div>
        <div className="text-xs text-neutral-500">geb. {new Date(r.kind_geboortedatum).toLocaleDateString('nl-NL')}</div>
      </div>
    )},
    { key: "ouder_naam", label: "Ouder", render: r => (
      <div>
        <div className="text-sm">{r.ouder_naam}</div>
        <div className="text-xs text-neutral-500">{r.ouder_email}</div>
      </div>
    )},
    { key: "voorkeur_locatie_id", label: "Voorkeur locatie", accessor: r => locMap[r.voorkeur_locatie_id] || "—" },
    { key: "voorkeur_startdatum", label: "Startdatum", render: r => r.voorkeur_startdatum ? new Date(r.voorkeur_startdatum).toLocaleDateString('nl-NL') : "—" },
    { key: "dagdelen_per_week", label: "Dagdelen/wk", render: r => <span className="font-mono">{r.dagdelen_per_week}</span> },
    { key: "status", label: "Status", render: r => <Badge variant="outline" className={`border ${STATUS_TONE[r.status]}`}>{r.status}</Badge> },
    { key: "aangemeld_op", label: "Aangemeld", render: r => new Date(r.aangemeld_op).toLocaleDateString('nl-NL') },
    ...(canWrite ? [{ key: "_act", label: "", sortable: false, render: r => (
      <div className="flex items-center gap-1 justify-end">
        <Button size="sm" variant="ghost" data-testid={`edit-wl-${r.id}`}
          onClick={(e) => { e.stopPropagation(); openEdit(r); }} className="h-8">
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <Button size="sm" variant="ghost" data-testid={`delete-wl-${r.id}`}
          onClick={(e) => { e.stopPropagation(); removeRow(r); }}
          className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8">
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    )}] : []),
  ];

  return (
    <div>
      <PageHeader
        title="Wachtlijst"
        subtitle="Overzicht van aanmeldingen — beheer, prioriteer en zet om naar contract."
        testid="wachtlijst-header"
        actions={
          <div className="flex items-center gap-2">
            {canExport && (
              <div className="flex items-center gap-1 border-r border-neutral-200 pr-2 mr-2">
                {["koi", "lrk", "gemeente", "peutermonitor"].map(e => (
                  <Button key={e} variant="outline" size="sm" onClick={() => download(e)} data-testid={`export-${e}`}>
                    <Download className="h-3 w-3 mr-1" /> {e.toUpperCase()}
                  </Button>
                ))}
              </div>
            )}
            {canWrite && (
              <Dialog open={open} onOpenChange={(v) => { if (!v) closeDialog(); else setOpen(true); }}>
                <DialogTrigger asChild>
                  <Button data-testid="add-wachtlijst-btn" style={{ background: "var(--sage)" }} className="text-white">
                    <Plus className="h-4 w-4 mr-1" /> Nieuwe aanmelding
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white max-w-lg">
                  <DialogHeader>
                    <DialogTitle>{editingId ? "Aanmelding wijzigen" : "Nieuwe aanmelding"}</DialogTitle>
                    <DialogDescription>{editingId ? "Pas de gegevens van deze aanmelding aan." : "Voeg een kind toe aan de wachtlijst."}</DialogDescription>
                  </DialogHeader>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Voornaam kind</Label><Input value={form.kind_voornaam} onChange={e => setForm({ ...form, kind_voornaam: e.target.value })} /></div>
                    <div><Label>Achternaam kind</Label><Input value={form.kind_achternaam} onChange={e => setForm({ ...form, kind_achternaam: e.target.value })} /></div>
                    <div><Label>Geboortedatum</Label><DateField value={form.kind_geboortedatum} onChange={(v) => setForm({ ...form, kind_geboortedatum: v })} data-testid="wachtlijst-geboortedatum" /></div>
                    <div><Label>Startdatum voorkeur</Label><DateField value={form.voorkeur_startdatum || ""} onChange={(v) => setForm({ ...form, voorkeur_startdatum: v })} data-testid="wachtlijst-voorkeur-startdatum" /></div>
                    <div className="col-span-2"><Label>Naam ouder</Label><Input value={form.ouder_naam} onChange={e => setForm({ ...form, ouder_naam: e.target.value })} /></div>
                    <div><Label>E-mail ouder</Label><Input type="email" value={form.ouder_email || ""} onChange={e => setForm({ ...form, ouder_email: e.target.value })} /></div>
                    <div><Label>Telefoon ouder</Label><Input value={form.ouder_telefoon || ""} onChange={e => setForm({ ...form, ouder_telefoon: e.target.value })} /></div>
                    <div>
                      <Label>Voorkeur locatie</Label>
                      <Select value={form.voorkeur_locatie_id || ""} onValueChange={v => setForm({ ...form, voorkeur_locatie_id: v })}>
                        <SelectTrigger><SelectValue placeholder="Kies" /></SelectTrigger>
                        <SelectContent className="bg-white">
                          {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Dagdelen/week</Label><Input type="number" min="1" max="10" value={form.dagdelen_per_week} onChange={e => setForm({ ...form, dagdelen_per_week: parseInt(e.target.value) })} /></div>
                    <div className="col-span-2"><Label>Opmerking</Label><Textarea rows={2} value={form.opmerking || ""} onChange={e => setForm({ ...form, opmerking: e.target.value })} /></div>
                  </div>
                  <DialogFooter>
                    <Button variant="ghost" onClick={closeDialog}>Annuleren</Button>
                    <Button onClick={submit} style={{ background: "var(--sage)" }} className="text-white" data-testid="wachtlijst-save">{editingId ? "Wijzigingen opslaan" : "Opslaan"}</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
        }
      />

      {suggestions.length > 0 && (
        <Card className="p-4 mb-6 bg-white shadow-none" data-testid="wachtlijst-suggestions">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="h-4 w-4 text-[color:var(--terracotta)]" />
            <div className="text-sm font-medium text-neutral-800">Plaatsingsvoorstellen ({suggestions.length})</div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {suggestions.slice(0, 6).map(s => (
              <div key={s.wachtlijst_id} className="p-3 rounded-md border border-neutral-200" data-testid={`sugg-${s.wachtlijst_id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="font-medium text-sm text-neutral-800">{s.kind_naam || "Onbekend"}</div>
                    <div className="text-xs text-neutral-500">
                      {s.leeftijd_maanden != null ? `${s.leeftijd_maanden} mnd` : "leeftijd onbekend"}
                      {s.gewenste_startdatum && ` · start ${new Date(s.gewenste_startdatum).toLocaleDateString('nl-NL')}`}
                    </div>
                  </div>
                  {!s.any_match && (
                    <Badge variant="outline" className="text-[10px] bg-neutral-100 text-neutral-500 border-neutral-200">Geen match</Badge>
                  )}
                </div>
                {s.any_match && (
                  <div className="space-y-1.5">
                    {s.top_matches.map((m, idx) => (
                      <div key={m.group_id} className="flex items-center gap-2 text-xs">
                        <Badge variant="outline" className="text-[9px] h-4 px-1 bg-neutral-50 text-neutral-600">#{idx + 1}</Badge>
                        <span className="font-medium truncate flex-1">{m.group_name}</span>
                        <span className="text-neutral-500">{m.location_type} · {m.location_name}</span>
                        <Badge variant="outline" className={`text-[10px] ${m.vrij > 3 ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-800 border-amber-200"}`}>
                          {m.vrij} vrij / {m.capacity}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          {suggestions.length > 6 && (
            <div className="text-xs text-neutral-500 mt-3">{suggestions.length - 6} extra voorstellen — verfijn filters in de wachtlijst-tabel hieronder.</div>
          )}
        </Card>
      )}

      <DataTable columns={columns} rows={rows} testid="wachtlijst-table" />

      {/* Match kandidaten voor vrijkomende plekken */}
      <VrijkomendePlekkenPanel />

      {/* Uit aanmeldingen (publieke intake) */}
      {aanmeldingenWl.length > 0 && (
        <Card className="p-4 mt-4 bg-white" data-testid="wl-uit-aanmeldingen">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-sm font-medium">Uit publieke aanmeldingen ({aanmeldingenWl.length})</div>
              <div className="text-xs text-neutral-500">Kandidaten die via het publieke aanmeldformulier zijn binnengekomen en op de wachtlijst zijn geplaatst.</div>
            </div>
            <Link to="/app/aanmeldingen" className="text-xs text-[color:var(--sage)] hover:underline flex items-center gap-1">
              Naar aanmeldingen-dashboard <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {aanmeldingenWl.slice(0, 12).map(a => (
              <Link to={`/app/aanmeldingen`} key={a.id} className="block p-3 border rounded hover:bg-neutral-50 transition-colors" data-testid={`wl-aanmelding-${a.id}`}>
                <div className="font-medium text-sm">{a.kind_voornaam} {a.kind_achternaam}</div>
                <div className="text-xs text-neutral-500 truncate">{a.ouder_voornaam} {a.ouder_achternaam} · {a.ouder_email}</div>
                <div className="text-xs text-neutral-500 mt-1">{a.location_name} · start {a.gewenste_startdatum}</div>
                <div className="text-[10px] text-neutral-400 mt-1">Ref {a.id.slice(0, 8).toUpperCase()}</div>
              </Link>
            ))}
          </div>
          {aanmeldingenWl.length > 12 && (
            <div className="text-xs text-neutral-500 mt-2">+ {aanmeldingenWl.length - 12} meer — beheer via het aanmeldingen-dashboard.</div>
          )}
        </Card>
      )}
    </div>
  );
}

function init() {
  return {
    kind_voornaam: "", kind_achternaam: "", kind_geboortedatum: "",
    ouder_naam: "", ouder_email: "", ouder_telefoon: "",
    voorkeur_locatie_id: "", voorkeur_startdatum: "",
    dagdelen_per_week: 2, opmerking: "", status: "aangemeld",
  };
}

function VrijkomendePlekkenPanel() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(60);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/aanmelding-vrijkomend?days=${days}`);
      setData(data.vrijkomend || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  }, [days]);
  useEffect(() => { load(); }, [load]);

  const stuurAanbod = async (aanmeldingId, plek) => {
    setBusy(aanmeldingId);
    try {
      // Maak direct een aanbod op basis van de eindigende plek
      const startdatum = plek.end_date;
      await api.post(`/aanmeldingen/${aanmeldingId}/aanbod`, {
        group_id: plek.group_id,
        startdatum,
        dagen: [], // wordt door backend uit aanmelding overgenomen indien leeg
        uurtarief: 9.65,
        geldig_dagen: 7,
        boodschap: `Er komt een plek vrij in groep ${plek.group_name || ""} vanaf ${startdatum}. Wij bieden deze plek graag aan uw kind aan.`,
      });
      toast.success("Aanbod verstuurd naar ouder");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Aanbod maken mislukt"); }
    setBusy(null);
  };

  if (loading) return <Card className="p-4 mt-4 bg-white text-xs text-neutral-500">Zoeken naar vrijkomende plekken…</Card>;
  if (data.length === 0) {
    return (
      <Card className="p-4 mt-4 bg-white" data-testid="vrijkomend-empty">
        <div className="flex items-center justify-between">
          <div className="text-sm">
            <div className="font-medium flex items-center gap-1.5"><Calendar className="h-4 w-4" style={{ color: "var(--sage)" }} /> Vrijkomende plekken</div>
            <div className="text-xs text-neutral-500">Geen contracten die aflopen binnen {days} dagen.</div>
          </div>
          <Select value={String(days)} onValueChange={v => setDays(parseInt(v))}>
            <SelectTrigger className="w-32 h-8 text-xs" data-testid="vrijkomend-days"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              {[30, 60, 90, 180].map(d => <SelectItem key={d} value={String(d)}>{d} dagen</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-4 mt-4 bg-white" data-testid="vrijkomend-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-sm font-medium flex items-center gap-1.5">
            <Calendar className="h-4 w-4" style={{ color: "var(--sage)" }} />
            Vrijkomende plekken · match-suggesties ({data.length})
          </div>
          <div className="text-xs text-neutral-500">Contracten die aflopen binnen {days} dagen — met top-3 wachtlijst-kandidaten per plek.</div>
        </div>
        <Select value={String(days)} onValueChange={v => setDays(parseInt(v))}>
          <SelectTrigger className="w-32 h-8 text-xs" data-testid="vrijkomend-days"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            {[30, 60, 90, 180].map(d => <SelectItem key={d} value={String(d)}>{d} dagen</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-3">
        {data.map(plek => (
          <div key={plek.contract_id} className="p-3 border rounded-md" data-testid={`vrijkomend-${plek.contract_id}`}>
            <div className="flex items-start justify-between mb-2 flex-wrap gap-2">
              <div>
                <div className="text-sm font-medium">
                  {plek.kind_voornaam} {plek.kind_achternaam} vertrekt uit <span className="text-neutral-700">{plek.group_name || "—"}</span>
                </div>
                <div className="text-xs text-neutral-500">
                  Locatie {plek.location_name} · einddatum <strong>{plek.end_date}</strong>
                </div>
              </div>
              <Badge variant="outline" className="bg-amber-50 border-amber-300 text-amber-700">Plek komt vrij</Badge>
            </div>
            {plek.top_matches.length === 0 ? (
              <div className="text-xs text-neutral-400 italic">Geen passende kandidaten op de wachtlijst voor deze locatie.</div>
            ) : (
              <div className="space-y-1">
                {plek.top_matches.map(m => (
                  <div key={m.id} className="flex items-center justify-between p-2 rounded bg-neutral-50 text-xs" data-testid={`match-${m.id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{m.kind_voornaam} {m.kind_achternaam}</span>
                      <span className="text-neutral-500">· {new Date(m.kind_geboortedatum).toLocaleDateString('nl-NL')}</span>
                      <span className="text-neutral-500">· wil start {m.gewenste_startdatum}</span>
                      {!m.match_age_fit && <Badge variant="outline" className="bg-amber-50 border-amber-200 text-amber-700 text-[10px]">Leeftijd mismatch</Badge>}
                      <Badge variant="outline" className="text-[10px]">score {m.match_score}</Badge>
                    </div>
                    <Button size="sm" onClick={() => stuurAanbod(m.id, plek)}
                      disabled={busy === m.id || !plek.group_id}
                      style={{ background: "var(--sage)" }} className="text-white h-7 text-xs"
                      data-testid={`match-aanbod-${m.id}`}>
                      {busy === m.id ? "Bezig…" : <><Send className="h-3 w-3 mr-1" /> Direct aanbod</>}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}
